/**
 * Projects advanced-filter document — a versioned value the web filter panel
 * (`ProjectFilterPanel`) edits and evaluates CLIENT-SIDE over the complete
 * project index. One schema + ONE evaluator, both in `@releve/shared` (the
 * timesheets/Vault template).
 *
 * Unlike the other index grids, the document is applied at PAGE level: the
 * same filtered rows feed the list, the map and the timeline, so a filter
 * narrows all three views. Search stays list-only (the grid shell owns it).
 *
 * The document is AD HOC (component state for the session). The page starts
 * on `DEFAULT_PROJECT_FILTER_DOCUMENT` (planning + active — the live work);
 * Clear resets to `EMPTY_PROJECT_FILTER_DOCUMENT` (every project).
 *
 * ── v1 vocabulary ───────────────────────────────────────────────────────────
 *  - `project.status`          string family over the raw status token.
 *  - `project.project_type_id` string family over the type ID (not its name:
 *                              project types are dual-scope, so a global and
 *                              an org type can share a name; the panel labels
 *                              options by name from the loaded rows).
 *  - `project.place_id`        ARRAY family over the ids of the row's linked
 *                              places — `array_has_any` matches a project
 *                              linked to ANY selected place.
 * No date family: the timeline's period picker is a viewport, not a filter.
 *
 * ── The "No status" option (the NULL token) ────────────────────────────────
 * `projects.status` is nullable. NULL is spelled with the string family's
 * existing empty arm — never a sentinel string such as `'__none__'`, which
 * would be a second spelling of the same state:
 *   "No status" only            → { op: 'is_empty' }
 *   statuses + "No status"      → { op: 'in', values, include_empty: true }
 * The same arms serve a project whose type is RLS-hidden. A project with no
 * linked place is reachable through `array_is_empty` on `project.place_id`.
 *
 * Per docs/INDEX_GRID_PATTERN.md step 9b, the grid runs the panel only — ZERO
 * filterable columns. Fields are `project.<field>` over the MAPPED rows.
 */
import { z } from "zod";
import { makeArrayConditionSchema, makeFilterDocumentSchema, makeStringConditionSchema, matchesArrayOp, matchesStringOp, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1)
// ---------------------------------------------------------------------------
export const PROJECT_FILTER_STRING_FIELDS = [
    "project.status",
    "project.project_type_id",
];
export const PROJECT_FILTER_ARRAY_FIELDS = ["project.place_id"];
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
/**
 * Two families with DISJOINT op sets ('in'/'is_empty' vs
 * 'array_has_any'/'array_is_empty'), so `matchesCondition` narrows on `op`
 * alone. "Nothing checked" is NO condition — an `in` / `array_has_any` with
 * zero values is rejected by the schema.
 */
const stringConditionSchema = makeStringConditionSchema(PROJECT_FILTER_STRING_FIELDS);
const arrayConditionSchema = makeArrayConditionSchema(PROJECT_FILTER_ARRAY_FIELDS);
export const projectFilterConditionSchema = z.union([
    stringConditionSchema,
    arrayConditionSchema,
]);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const projectFilterDocumentSchema = makeFilterDocumentSchema(projectFilterConditionSchema);
/** The empty document — "no filters active" (what Clear resets to). */
export const EMPTY_PROJECT_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/** The page's starting document — live work only (planning + active). */
export const DEFAULT_PROJECT_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [{ field: "project.status", op: "in", values: ["planning", "active"] }],
};
/*
 * One accessor per field FAMILY, each an exhaustive switch with NO default:
 * a new vocabulary entry is a TS2366 until it is handled here.
 */
function stringValue(row, field) {
    switch (field) {
        case "project.status":
            return row.status ?? "";
        case "project.project_type_id":
            return row.project_type_id ?? "";
    }
}
function arrayValue(row, field) {
    switch (field) {
        case "project.place_id":
            return row.places.map((p) => p.place_id);
    }
}
// The two condition families have disjoint op sets, so op alone narrows.
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
        case "array_has_any":
        case "array_is_empty":
            return matchesArrayOp(c.op, arrayValue(row, c.field), c.values);
    }
}
/**
 * Apply a filter document to project rows (AND of all conditions — v1
 * `match` is 'all' only). An empty document returns the input array unchanged
 * (identity — callers can rely on reference equality to skip re-renders).
 */
export function applyProjectFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveProjectConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=projectFilters.js.map